import {
  BookOpen,
  Building2,
  CalendarPlus,
  ClipboardCheck,
  ClipboardPen,
  CodeXml,
  Compass,
  Crown,
  GraduationCap,
  Info,
  Layers,
  Mail,
  SquareTerminal,
  Tag,
  Trophy,
  Users,
  type LucideIcon,
} from "lucide-react";

/**
 * `SITE-NAV01`: what the site bar links to, in one place, so the desktop bar,
 * the More panel, the phone menu and the search's page list cannot drift apart.
 *
 * Labels and descriptions are message keys under `nav`; the components
 * translate them. `docs/11` §4 fixes the six primary links and their order
 * (`LN-NAV01`); the More panel's four groups are the 2026-09-24 canvas, decided
 * by the founder with D1.
 */

export type PrimaryKey = "courses" | "learn" | "playground" | "judge" | "contests" | "quizExams";

export type NavEntry = {
  id: string;
  /** Message key under `nav`. */
  label: string;
  /** Message key under `nav`, the one line the More panel and the phone menu show. */
  description: string;
  href: string;
  icon: LucideIcon;
  /** Path prefixes, besides `href` itself, that mean the visitor is on this entry's page. */
  activePrefixes?: string[];
};

export type PrimaryEntry = NavEntry & { key: PrimaryKey };

export type MoreGroup = {
  id: "learn" | "practice" | "teach" | "progsity";
  /** Message key under `nav`. */
  heading: string;
  entries: NavEntry[];
};

/** The Learn C track: where "Start free" goes (D2); its first lessons open without an account. */
export function startFreeHref(locale: string): string {
  return `/${locale}/learn/c`;
}

export function primaryEntries(locale: string): PrimaryEntry[] {
  const base = `/${locale}`;
  return [
    { key: "courses", id: "courses", label: "courses", description: "desc.courses", href: `${base}/courses`, icon: GraduationCap },
    { key: "learn", id: "learn", label: "learn", description: "desc.learn", href: `${base}/learn`, icon: BookOpen },
    { key: "playground", id: "playground", label: "playground", description: "desc.playground", href: `${base}/playground`, icon: SquareTerminal },
    { key: "judge", id: "judge", label: "judge", description: "desc.judge", href: `${base}/judge`, icon: CodeXml },
    { key: "contests", id: "contests", label: "contests", description: "desc.contests", href: `${base}/contests`, icon: Trophy },
    { key: "quizExams", id: "quizExams", label: "quizExams", description: "desc.quizExams", href: `${base}/quiz-exams`, icon: ClipboardCheck },
  ];
}

/**
 * The More panel. It repeats the primary links on purpose: the panel is the
 * whole site on one screen, each with the line that says what it is, and the
 * bar alone never said that.
 *
 * `createQuizHref` is decided by the caller: a signed-out teacher lands on the
 * Quiz & Exams pitch, which carries its own sign-in, rather than on a login
 * wall (the 2026-09-03 review).
 */
export function moreGroups(locale: string, createQuizHref: string): MoreGroup[] {
  const base = `/${locale}`;
  const primary = new Map(primaryEntries(locale).map((entry) => [entry.key, entry]));
  const pick = (key: PrimaryKey): NavEntry => primary.get(key)!;
  return [
    {
      id: "learn",
      heading: "moreGroups.learn",
      entries: [
        pick("courses"),
        pick("learn"),
        { id: "programs", label: "programs", description: "moreProgramsDesc", href: `${base}/programs`, icon: Layers },
        { id: "careerPath", label: "careerPath", description: "moreCareerPathDesc", href: `${base}/career`, icon: Compass },
      ],
    },
    {
      id: "practice",
      heading: "moreGroups.practice",
      entries: [
        pick("playground"),
        pick("judge"),
        pick("contests"),
        { id: "leaderboard", label: "leaderboard", description: "desc.leaderboard", href: `${base}/judge/leaderboard`, icon: Crown },
      ],
    },
    {
      id: "teach",
      heading: "moreGroups.teach",
      entries: [
        {
          id: "createQuiz",
          label: "ftCreateQuizTitle",
          description: "ftCreateQuizDesc",
          href: createQuizHref,
          icon: ClipboardPen,
          activePrefixes: [`${base}/dashboard/author-studio`],
        },
        {
          id: "quizPricing",
          label: "ftQuizPricingTitle",
          description: "ftQuizPricingDesc",
          href: `${base}/quiz/pricing`,
          icon: Tag,
          activePrefixes: [`${base}/quiz-exams/pricing`],
        },
        { id: "institutional", label: "ftInstitutionalTitle", description: "ftInstitutionalDesc", href: `${base}/for-institutions`, icon: Building2 },
        { id: "hostContest", label: "hostContest", description: "desc.hostContest", href: `${base}/contests/host`, icon: CalendarPlus },
      ],
    },
    {
      id: "progsity",
      heading: "moreGroups.progsity",
      entries: [
        { id: "about", label: "moreAboutTitle", description: "moreAboutDesc", href: `${base}/about`, icon: Info },
        { id: "contact", label: "moreContactTitle", description: "moreContactDesc", href: `${base}/contact`, icon: Mail },
        { id: "ambassador", label: "moreAmbassadorTitle", description: "moreAmbassadorDesc", href: `${base}/ambassador`, icon: Users },
      ],
    },
  ];
}

export function pathMatches(pathname: string, target: string): boolean {
  return pathname === target || pathname.startsWith(`${target}/`);
}

export function isEntryActive(entry: NavEntry, pathname: string): boolean {
  return [entry.href, ...(entry.activePrefixes ?? [])].some((prefix) => pathMatches(pathname, prefix));
}

/**
 * The More button reads as current on a page only the panel links to. A page
 * the bar already shows (Courses, the Judge) lights its own link instead, so
 * two things never claim the same page.
 */
export function isMoreActive(groups: MoreGroup[], primary: PrimaryEntry[], pathname: string): boolean {
  if (primary.some((entry) => isEntryActive(entry, pathname))) return false;
  const primaryIds = new Set(primary.map((entry) => entry.id));
  return groups.some((group) =>
    group.entries.some((entry) => !primaryIds.has(entry.id) && isEntryActive(entry, pathname)),
  );
}
